/**
 * Typed endpoint wrappers. One function per backend route, returning typed
 * responses. Keeps components free of raw fetch calls and string paths.
 */

import { api } from "./api-client";
import type {
  LoginResponse,
  MeResponse,
  RefreshResponse,
  SelectInstanceResponse,
  InstancePublic,
  UserPublic,
  AccessGrantPublic,
  UiRole,
} from "@/types";

// ── Auth ────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (username: string, password: string, instanceName?: string) =>
    api.post<LoginResponse>("/api/auth/login", { username, password, instanceName }, { noAuth: true }),
  selectInstance: (instanceName: string) =>
    api.post<SelectInstanceResponse>("/api/auth/select-instance", { instanceName }),
  refresh: () =>
    api.post<RefreshResponse>("/api/auth/refresh", {}, { noAuth: true, noRefresh: true }),
  logout: () => api.post<{ ok: true }>("/api/auth/logout", {}, { noRefresh: true }),
  me: () => api.get<MeResponse>("/api/auth/me"),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: true }>("/api/auth/change-password", { currentPassword, newPassword }),
};

// ── Audit ───────────────────────────────────────────────────────────────────
export interface AuditEvent {
  eventId: string;
  occurredAt: string;
  actorUserId: string | null;
  actorUsername: string | null;
  instanceId: string | null;
  action: string;
  method: string;
  path: string;
  status: number;
  ip: string | null;
  detail: string | null;
}

export const auditApi = {
  list: (days = 7) => api.get<{ events: AuditEvent[] }>(`/api/audit?days=${days}`),
};

// ── Instances ───────────────────────────────────────────────────────────────
export interface CreateInstanceInput {
  name: string;
  apiUrl: string;
  region: string;
  envLabel: string;
  color?: string | null;
  superadminKey: string;
}
export interface UpdateInstanceInput {
  apiUrl?: string;
  region?: string;
  envLabel?: string;
  color?: string | null;
  isActive?: boolean;
  superadminKey?: string;
}

export const instanceApi = {
  list: () => api.get<{ instances: InstancePublic[] }>("/api/instances"),
  create: (input: CreateInstanceInput) =>
    api.post<InstancePublic>("/api/instances", input),
  get: (id: string) => api.get<InstancePublic>(`/api/instances/${id}`),
  update: (id: string, input: UpdateInstanceInput) =>
    api.patch<InstancePublic>(`/api/instances/${id}`, input),
  delete: (id: string) => api.del<{ ok: true }>(`/api/instances/${id}`),
};

// ── Users + grants ──────────────────────────────────────────────────────────
export interface CreateUserInput {
  username: string;
  email?: string | null;
  role: UiRole;
  password: string;
  mustChangePassword?: boolean;
}
export interface UpdateUserInput {
  email?: string | null;
  role?: UiRole;
  isActive?: boolean;
}
export interface CreateGrantInput {
  instanceId: string;
  tenantScope: string;
  grantRole: UiRole;
  tenantName?: string | null;
  tenantAdminKey?: string;
}

export const userApi = {
  list: () => api.get<{ users: UserPublic[] }>("/api/users"),
  create: (input: CreateUserInput) => api.post<UserPublic>("/api/users", input),
  get: (id: string) => api.get<UserPublic>(`/api/users/${id}`),
  update: (id: string, input: UpdateUserInput) =>
    api.patch<UserPublic>(`/api/users/${id}`, input),
  delete: (id: string) => api.del<{ ok: true }>(`/api/users/${id}`),
  resetPassword: (id: string, newPassword: string) =>
    api.post<{ ok: true }>(`/api/users/${id}/reset-password`, { newPassword }),
  listGrants: (id: string) =>
    api.get<{ grants: AccessGrantPublic[] }>(`/api/users/${id}/grants`),
  createGrant: (id: string, input: CreateGrantInput) =>
    api.post<AccessGrantPublic>(`/api/users/${id}/grants`, input),
  updateGrant: (userId: string, instanceId: string, input: Partial<CreateGrantInput>) =>
    api.patch<AccessGrantPublic>(`/api/users/${userId}/grants/${instanceId}`, input),
  deleteGrant: (userId: string, instanceId: string) =>
    api.del<{ ok: true }>(`/api/users/${userId}/grants/${instanceId}`),
};
